import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { CreatedSelectFilter } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys } from '../../../../../common/constants/product-keys';
import type { SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';
import { getCachedThirdPartyFilterOptions } from '../utils/third-party-filter-options-cache';

const salesforceProducts: ProductKeys[] = [ProductKeys.Salesforce];
const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('leadStatus');
const leadStatusProducts: ProductKeys[] = Array.from(
	new Set<ProductKeys>([...salesforceProducts, ...products3PFromConfig]),
);

const fetchDynamicLeadStatusOptions = async (
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const cloudId = config.cloudId;
	const datasourceId = config.selectedDatasource?.datasourceId;

	if (!cloudId || !datasourceId) {
		return [];
	}

	try {
		const result = await getCachedThirdPartyFilterOptions(config);

		if (!result) {
			return [];
		}

		const leadStatuses = result.filters.find((f) => f.name === 'leadStatuses');
		if (!leadStatuses || leadStatuses.values.length === 0) {
			return [];
		}

		return leadStatuses.values.map((leadStatus) => ({
			value: leadStatus.name,
			trackingKey: leadStatus.name,
			queryParamValue: leadStatus.name,
			isDefault: false,
			label: leadStatus.label ?? leadStatus.name,
			product: config.product,
			isSelected: false,
			isSuggested: false,
		}));
	} catch (e) {
		return [];
	}
};

const fetchLeadStatusOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> => {
	return fetchDynamicLeadStatusOptions(config);
};

const fetchQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const allOptions = await fetchLeadStatusOptions(config);
	const selected = new Set(queryParams);
	const result = allOptions.filter((option) => selected.has(option.queryParamValue));
	return result;
};

export const filter: CreatedSelectFilter<'leadStatus'> = createMultiSelectFilter({
	id: 'leadStatus',
	requiresTopLevel3PDatasource: true,
	products: leadStatusProducts,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'leadStatuses',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return fetchLeadStatusOptions(config);
		},
	},
});
